import { useState, useEffect, useCallback } from 'react';
import { getStaffReadinessStatus, publishStaffReadinessSnapshot, type GetStaffReadinessStatusOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle2, XCircle, Clock, CloudOff, Loader2, ArrowUpFromLine } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'Synced':
      return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Synced</Badge>;
    case 'Syncing':
      return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Syncing</Badge>;
    case 'Failed':
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    default:
      return <Badge variant="secondary"><CloudOff className="w-3 h-3 mr-1" />Never sent</Badge>;
  }
}

function Timestamp({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-mono text-xs">
        {value ? formatDistanceToNow(parseISO(value), { addSuffix: true }) : '—'}
      </span>
    </div>
  );
}

export default function StaffReadinessIntegration() {
  const [data, setData] = useState<GetStaffReadinessStatusOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadStatus = useCallback(() => {
    setLoading(true);
    getStaffReadinessStatus({}).then(setData).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await publishStaffReadinessSnapshot({});
      if (result.success) {
        toast.success(`Snapshot synced — ${result.staffCount} verified staff records sent`);
      } else {
        toast.error(result.error || 'Sync failed — check error details');
      }
      loadStatus();
    } catch {
      toast.error('Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="border border-border rounded-lg bg-card p-5 space-y-3">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  const status = data?.status || 'Never Sent';

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
            <ArrowUpFromLine className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-foreground">BMS Staff Readiness Feed</h3>
            <p className="text-xs text-muted-foreground">Publishes safe operational data to phone.rbcomms.co.uk</p>
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="px-5 py-4 space-y-3">
        <Timestamp label="Last attempted" value={data?.lastAttempted ?? null} />
        <Timestamp label="Last succeeded" value={data?.lastSucceeded ?? null} />

        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Staff count</span>
          <span className="text-foreground font-mono text-xs">{data?.staffCount ?? '—'}</span>
        </div>

        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Snapshot ID</span>
          <span className="text-foreground font-mono text-xs truncate max-w-[180px]">{data?.lastSnapshotId || '—'}</span>
        </div>

        {status === 'Failed' && data?.errorMessage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2 text-xs text-destructive">
            <p className="font-medium mb-1">Error ({data.consecutiveFailures} consecutive failure{data.consecutiveFailures !== 1 ? 's' : ''})</p>
            <p className="break-all">{data.errorMessage.slice(0, 300)}</p>
          </div>
        )}

        <p className="text-xs text-muted-foreground pt-1">
          Daily auto-sync at 06:00 BST. Manual sync available below.
        </p>
      </div>

      <div className="px-5 py-3 border-t border-border flex gap-2">
        <Button size="sm" onClick={handleSync} disabled={syncing}>
          {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
        <Button size="sm" variant="outline" onClick={loadStatus} disabled={loading}>
          <Clock className="w-3.5 h-3.5 mr-1.5" />Refresh status
        </Button>
      </div>
    </div>
  );
}
